import type { CodeableConcept, Observation, ObservationComponent } from "@medplum/fhirtypes";
import {
  asFindingRecordId,
  asMemberId,
  asPatientId,
  asPractitionerId,
  asProcedureId,
  asVisitId,
  type FindingAnswer,
  type FindingFacts,
  parsePinnedCanonical,
} from "@repo/domain";
import {
  CHARTING_OBSERVATION_KIND_SYSTEM,
  codeSystemUrl,
  extensionUrl,
  FINDING_LINEAGE_SYSTEM,
  FINDING_STATE_SYSTEM,
  OBSERVATION_CATEGORY_SYSTEM,
} from "@repo/fhir-systems";
import {
  DIAGNOSTIC_PROCEDURE_ENTITY_ID_SYSTEM,
  FINDING_ENTITY_ID_SYSTEM,
  PATIENT_ENTITY_ID_SYSTEM,
  PRACTITIONER_ENTITY_ID_SYSTEM,
  VISIT_ENTITY_ID_SYSTEM,
} from "@repo/fhir-systems/identifiers";
import { valueFromObservation, valueToObservation } from "../lib/observation-value";
import { EXAM_FINDING_OPTION_SYSTEM } from "./finding-option-system";
import { oneOf, required } from "./read";
import { domainIdFromReference, logicalReferences, type ReferenceWriter } from "./references";

// ============================================================
// Finding ⇄ Observation, category exam
//
// DNP uses the R4 absent reason not-performed. UTP uses error plus our
// unable-to-perform coding: attempted but unsuccessful, distinct from DNP.
// Unanswered (a comment with no answer yet) is preliminary with our unanswered
// coding; the coding, not the status, survives retraction.
// A pinned canonical records what was offered; encounter and effectiveDateTime
// record where and when the clinician made the judgement. Multiple-choice
// answers always use components, including when only one option was selected.
// A measured answer is a component per value, coded in the finding component
// system; that coding, not a stored kind, is what reads it back as measured.
// Its display is the component's label from the pinned revision, so the row
// reads without the definition.
// Cardinality is a choice fact, so a measured answer does not store it.
// ============================================================

export const FINDING_COMPONENT_SYSTEM = codeSystemUrl("finding-component");
export const FINDING_PROFILE = extensionUrl("finding-observation");
export const FINDING_DEFINITION_URL = extensionUrl("finding-instantiates-canonical");
export const FINDING_TRACKED_URL = extensionUrl("finding-tracked-for-progress");
export const FINDING_CARDINALITY_URL = extensionUrl("finding-cardinality");
export { FINDING_LINEAGE_SYSTEM, FINDING_STATE_SYSTEM } from "@repo/fhir-systems";
export const FINDING_RESERVED_SYSTEM = codeSystemUrl("exam-reserved-response");
const OWNED = new Set<string>([FINDING_DEFINITION_URL, FINDING_TRACKED_URL, FINDING_CARDINALITY_URL]);
const absentSystem = "http://terminology.hl7.org/CodeSystem/data-absent-reason";
const selection = (code: string): CodeableConcept => ({
  coding: [{ system: code === "other" ? FINDING_RESERVED_SYSTEM : EXAM_FINDING_OPTION_SYSTEM, code }],
});
const isMeasuredComponent = (component: ObservationComponent): boolean =>
  component.code.coding?.some((coding) => coding.system === FINDING_COMPONENT_SYSTEM) === true;

type AnswerSlots = Pick<
  Observation,
  "valueQuantity" | "valueInteger" | "valueBoolean" | "valueString" | "valueCodeableConcept" | "component"
>;
/**
 * Every slot an answer may occupy, written as cleared. Callers merge this row
 * over its prior with `Object.assign` (the data-store's optimistic writes), so
 * an absent slot would keep the previous answer there.
 */
const CLEARED: Record<keyof AnswerSlots, undefined> = {
  valueQuantity: undefined,
  valueInteger: undefined,
  valueBoolean: undefined,
  valueString: undefined,
  valueCodeableConcept: undefined,
  component: undefined,
};

function answerSlots(
  answer: FindingAnswer | undefined,
  cardinality: FindingFacts["cardinality"],
  code: CodeableConcept,
): AnswerSlots {
  if (!answer) return {};
  if (answer.kind === "Measured") {
    return {
      component: answer.values.map((value) => ({
        code: { coding: [{ system: FINDING_COMPONENT_SYSTEM, code: value.componentId, display: value.label }] },
        ...valueToObservation(value, EXAM_FINDING_OPTION_SYSTEM),
      })),
    };
  }
  if (cardinality === "Multiple")
    return { component: answer.selections.map((value) => ({ code, valueCodeableConcept: selection(value) })) };
  return {
    valueCodeableConcept: selection(
      required({ resourceType: "Observation", status: "final", code: {} }, "selection", answer.selections[0]),
    ),
  };
}

export function writeFindingRow(
  facts: FindingFacts,
  prior: Observation | null,
  references: ReferenceWriter = logicalReferences,
): Observation {
  const code = { coding: [{ system: FINDING_LINEAGE_SYSTEM, code: facts.definition.url }] };
  const state = facts.state;
  const answer = state.kind === "Performed" ? state.answer : undefined;
  return {
    resourceType: "Observation",
    ...(prior
      ? {}
      : { meta: { profile: [FINDING_PROFILE], tag: [{ system: CHARTING_OBSERVATION_KIND_SYSTEM, code: "finding" }] } }),
    identifier: [{ system: FINDING_ENTITY_ID_SYSTEM, value: facts.id }],
    status:
      facts.status === "enteredInError" ? "entered-in-error" : state.kind === "Unanswered" ? "preliminary" : "final",
    category: [{ coding: [{ system: OBSERVATION_CATEGORY_SYSTEM, code: "exam" }] }],
    code,
    subject: references.identity("Patient", PATIENT_ENTITY_ID_SYSTEM, facts.patient),
    encounter: references.memberType("Encounter", VISIT_ENTITY_ID_SYSTEM, facts.visit),
    effectiveDateTime: facts.recordedAt,
    performer: [references.identity("Practitioner", PRACTITIONER_ENTITY_ID_SYSTEM, facts.clinician)],
    partOf: facts.procedure
      ? [references.identity("Procedure", DIAGNOSTIC_PROCEDURE_ENTITY_ID_SYSTEM, facts.procedure)]
      : undefined,
    note: facts.comment === undefined ? undefined : [{ text: facts.comment }],
    extension: [
      ...(prior?.extension ?? []).filter((ext) => !OWNED.has(ext.url)),
      { url: FINDING_DEFINITION_URL, valueCanonical: `${facts.definition.url}|${facts.definition.version}` },
      ...(facts.cardinality ? [{ url: FINDING_CARDINALITY_URL, valueCode: facts.cardinality }] : []),
      ...(facts.trackedForProgress ? [{ url: FINDING_TRACKED_URL, valueBoolean: true }] : []),
    ],
    ...CLEARED,
    ...answerSlots(answer, facts.cardinality, code),
    dataAbsentReason:
      state.kind === "Performed"
        ? undefined
        : {
            coding:
              state.kind === "Unanswered"
                ? [{ system: FINDING_STATE_SYSTEM, code: "unanswered" }]
                : [
                    { system: absentSystem, code: state.kind === "DidNotPerform" ? "not-performed" : "error" },
                    ...(state.kind === "UnableToPerform"
                      ? [{ system: FINDING_STATE_SYSTEM, code: "unable-to-perform" }]
                      : []),
                  ],
          },
  };
}

export function readFindingRow(row: Observation): FindingFacts {
  const measured = row.component?.some(isMeasuredComponent) === true;
  const declared = row.extension?.find((ext) => ext.url === FINDING_CARDINALITY_URL)?.valueCode;
  // A choice answer cannot be read without it; an unanswered measured row has none.
  const choiceAnswered = !measured && (row.valueCodeableConcept !== undefined || !!row.component?.length);
  const cardinality =
    measured || (declared === undefined && !choiceAnswered)
      ? undefined
      : oneOf(row, "cardinality", declared, ["Single", "Multiple"] as const);
  const absent = row.dataAbsentReason?.coding;
  const procedure = domainIdFromReference(DIAGNOSTIC_PROCEDURE_ENTITY_ID_SYSTEM, row.partOf?.[0]);
  const status = oneOf(row, "status", row.status, ["final", "preliminary", "entered-in-error"] as const);
  return {
    id: asFindingRecordId(
      required(row, "id", row.identifier?.find((id) => id.system === FINDING_ENTITY_ID_SYSTEM)?.value),
    ),
    patient: asPatientId(required(row, "patient", domainIdFromReference(PATIENT_ENTITY_ID_SYSTEM, row.subject))),
    visit: asVisitId(required(row, "visit", domainIdFromReference(VISIT_ENTITY_ID_SYSTEM, row.encounter))),
    clinician: asPractitionerId(
      required(row, "clinician", domainIdFromReference(PRACTITIONER_ENTITY_ID_SYSTEM, row.performer?.[0])),
    ),
    recordedAt: required(row, "recordedAt", row.effectiveDateTime),
    definition: required(
      row,
      "definition",
      parsePinnedCanonical(row.extension?.find((ext) => ext.url === FINDING_DEFINITION_URL)?.valueCanonical),
    ),
    status: status === "entered-in-error" ? "enteredInError" : "final",
    ...(cardinality ? { cardinality } : {}),
    state: absent?.some((coding) => coding.system === FINDING_STATE_SYSTEM && coding.code === "unanswered")
      ? { kind: "Unanswered" }
      : absent?.some((coding) => coding.system === FINDING_STATE_SYSTEM && coding.code === "unable-to-perform")
        ? { kind: "UnableToPerform" }
        : absent?.some((coding) => coding.system === absentSystem && coding.code === "not-performed")
          ? { kind: "DidNotPerform" }
          : measured
            ? {
                kind: "Performed",
                answer: {
                  kind: "Measured",
                  values: (row.component ?? []).map((component) => {
                    const coding = component.code.coding?.find((c) => c.system === FINDING_COMPONENT_SYSTEM);
                    return {
                      ...valueFromObservation(component, row, EXAM_FINDING_OPTION_SYSTEM),
                      componentId: asMemberId(required(row, "component.id", coding?.code)),
                      label: required(row, "component.label", coding?.display),
                    };
                  }),
                },
              }
            : {
                kind: "Performed",
                answer: {
                  kind: "Choice",
                  selections: (cardinality === "Multiple"
                    ? (row.component ?? []).map((component) => component.valueCodeableConcept)
                    : [row.valueCodeableConcept]
                  ).map((value) => required(row, "selection", value?.coding?.[0]?.code)),
                },
              },
    trackedForProgress: row.extension?.some((ext) => ext.url === FINDING_TRACKED_URL && ext.valueBoolean) === true,
    ...(row.note?.[0] ? { comment: row.note[0].text } : {}),
    ...(procedure ? { procedure: asProcedureId(procedure) } : {}),
  };
}
